import { StandardEvents } from '@shopify/events';
import { morph } from '@theme/morph';

/**
 * Keeps the tier cards in sync with the selected variant, and remembers that
 * the visitor has picked a tier.
 *
 * The card for the other tier links to that product with `?variant=` for the
 * weight currently chosen in the picker. When the picker fetches the product
 * page for a new variant it hands the parsed document to every listener via
 * the product-select event (StandardEvents.productSelect); that document already holds this section rendered
 * for the new weight, so the section simply morphs itself from its counterpart.
 * No link is computed client-side. The open/closed state of the collapsible
 * wrapper is client state, so it survives the morph.
 *
 * A click on a tier card sets a session flag; the next page reads it before
 * first paint (inline script in the section) and starts with the compact bar.
 */
const CHOSEN_KEY = 'wolv:tier-chosen';

class WolvTierCards extends HTMLElement {
  connectedCallback() {
    document.addEventListener(StandardEvents.productSelect, this.#onVariantUpdate);
    this.addEventListener('click', this.#onClick);
  }

  disconnectedCallback() {
    document.removeEventListener(StandardEvents.productSelect, this.#onVariantUpdate);
    this.removeEventListener('click', this.#onClick);
  }

  /**
   * @param {Event} event - The click event.
   */
  #onClick = (event) => {
    const target = /** @type {Element} */ (event.target);
    const card = target.closest('a.wolv-tier');
    if (!card) return;
    try {
      sessionStorage.setItem(CHOSEN_KEY, '1');
    } catch {
      // storage unavailable: the cards simply stay open on the next page
    }
    // The page's own tier: choosing it means staying here. Collapse to the
    // compact bar, drop the intro hero and jump to the top — no reload.
    if (card.hasAttribute('data-wolv-tier-current')) {
      event.preventDefault();
      document.documentElement.classList.add('wolv-tier-chosen');
      const details = this.querySelector('details.wolv-tiers__details');
      if (details) {
        details.removeAttribute('open');
        details.classList.add('wolv-tiers__details--chosen');
      }
      window.scrollTo({ top: 0, behavior: 'instant' });
    }
  };

  /**
   * @param {Event} event - The variant update event.
   */
  #onVariantUpdate = (event) => {
    // Horizon 4.x: the variant picker dispatches a product-select event whose
    // promise resolves with the freshly rendered product page (detail.html).
    // Selections inside product cards and quick add are not about this page.
    const target = event.target;
    if (!(target instanceof Element) || target.closest('product-card, quick-add-dialog')) return;

    /** @type {any} */ (event).promise
      ?.then((/** @type {any} */ result) => this.#apply(result?.detail?.html))
      .catch((/** @type {any} */ error) => {
        if (error?.name !== 'AbortError') console.warn('[wolv] product select rejected:', error);
      });
  };

  /**
   * @param {Document | undefined} html - The re-rendered product page.
   */
  #apply = (html) => {
    if (!html) return;

    const source = html.querySelector(`wolv-tier-cards[data-section-id="${this.dataset.sectionId}"]`);
    if (!source) return;

    const details = this.querySelector('details.wolv-tiers__details');
    const wasOpen = details?.hasAttribute('open');
    const wasChosen = details?.classList.contains('wolv-tiers__details--chosen');

    morph(this, source);

    const after = this.querySelector('details.wolv-tiers__details');
    if (after && details) {
      after.toggleAttribute('open', Boolean(wasOpen));
      after.classList.toggle('wolv-tiers__details--chosen', Boolean(wasChosen));
    }
  };
}

if (!customElements.get('wolv-tier-cards')) {
  customElements.define('wolv-tier-cards', WolvTierCards);
}
